import asyncEntityHandlers from '@anm/helpers/redux/asyncEntityHandlers';
import reduceFactory from '@anm/helpers/redux/reduceFactory';
import { getType } from '@anm/helpers/saga/typesafe-actions';
import { Reducer } from 'redux';

import { videoFolderActions } from '..';
import { authActions, AuthActions } from '../../auth';
import { moveProjectToOtherAsync } from '../actions';
import {
  addProject,
  cancelCreateProject,
  clearCreatedProject,
  clearDeleteProject,
  clearNewFolder,
  createFolderAsync,
  createProjectAsync,
  deleteFolderAsync,
  deleteProjectAsync,
  duplicateProjectAsync,
  expandFolderInTree,
  fetchAsync,
  fetchFolderTreeAsync,
  moveFolderAsync,
  moveProjectAsync,
  removeProjectFromList,
  renameFolderAsync,
  updateCreatedProject,
  updateEmbedStatus,
  updateProjectHostingId,
  updateProjectMetaAsync,
  updateProjectPreview
} from '../actions';
import { findAndUpdateFolder } from '../helpers';
import { VideoFolderActions, VideoFolderState } from '../types';

import { updateProjectAsync } from './../actions';
import deleteFolderReducer from './folder/deleteFolderReducer';
import fetchFolderReducer from './folder/fetchFolderReducer';
import folderTreeReducer from './folder/folderTreeReducer';
import moveFolderReducer from './folder/moveFolderReducer';
import newFolderReducer from './folder/newFolderReducer';
import renameFolderReducer from './folder/renameFolderReducer';
import createProjectReducer from './project/createProjectReducer';
import deleteProjectReducer from './project/deleteProjectReducer';
import duplicateProjectReducer from './project/duplicateProjectReducer';
import moveProjectReducer from './project/moveProjectReducer';
import moveProjectToOtherWorkspaceReducer from './project/moveProjectToOtherWorkspaceReducer';
import updateProjectMetaReducer from './project/updateProjectMetaReducer';
import updateProjectReducer from './project/updateProjectReducer';

export const initialVideoFolderState: VideoFolderState = {
  data: null,
  isError: false,
  error: null,
  isPending: false,
  newFolder: {
    params: null,
    data: null,
    isError: false,
    isPending: false
  },
  folderTree: {
    data: null,
    isError: false,
    isPending: false
  },
  moveFolder: {
    params: null,
    isError: false,
    isPending: false
  },
  renameFolder: {
    params: null,
    isError: false,
    isPending: false
  },
  fetchFolderById: {
    params: null,
    data: null,
    isError: false,
    isPending: false
  },
  deleteFolder: {
    params: null,
    isError: false,
    isPending: false
  },
  moveProject: {
    params: null,
    isError: false,
    isPending: false
  },
  moveProjectToOtherWorkspace: {
    params: null,
    isError: false,
    isPending: false
  },
  deleteProject: {
    projectId: null,
    isError: false,
    isPending: false
  },
  updateProjectMeta: {
    params: null,
    isError: false,
    isPending: false
  },
  updateProject: {
    projects: [],
    isError: false,
    isPending: false
  },
  duplicateProject: {
    data: {} as any,
    params: null,
    isError: false,
    isPending: false
  },
  createProject: {
    data: null,
    project: null,
    isError: false,
    isPending: false,
    cancelUploadProjectId: null
  }
};

const videoFolderReducer: Reducer<typeof initialVideoFolderState, VideoFolderActions | AuthActions> = (
  state = initialVideoFolderState,
  action
) => {
  switch (action.type) {
    case getType(authActions.logout):
      return initialVideoFolderState;

    case getType(createFolderAsync.request):
    case getType(createFolderAsync.success):
    case getType(createFolderAsync.failure):
    case getType(clearNewFolder):
      return newFolderReducer(state, action);

    case getType(fetchAsync.request):
    case getType(fetchAsync.success):
    case getType(fetchAsync.failure):
      return fetchFolderReducer(state, action);

    case getType(moveFolderAsync.request):
    case getType(moveFolderAsync.success):
    case getType(moveFolderAsync.failure):
      return moveFolderReducer(state, action);

    case getType(renameFolderAsync.request):
    case getType(renameFolderAsync.success):
    case getType(renameFolderAsync.failure):
      return renameFolderReducer(state, action);

    case getType(deleteFolderAsync.request):
    case getType(deleteFolderAsync.success):
    case getType(deleteFolderAsync.failure):
      return deleteFolderReducer(state, action);

    case getType(expandFolderInTree):
    case getType(fetchFolderTreeAsync.request):
    case getType(fetchFolderTreeAsync.success):
    case getType(fetchFolderTreeAsync.failure):
      return folderTreeReducer(state, action);

    case getType(moveProjectAsync.request):
    case getType(moveProjectAsync.success):
    case getType(moveProjectAsync.failure):
      return moveProjectReducer(state, action);

    case getType(moveProjectToOtherAsync.request):
    case getType(moveProjectToOtherAsync.success):
    case getType(moveProjectToOtherAsync.failure):
      return moveProjectToOtherWorkspaceReducer(state, action);

    case getType(deleteProjectAsync.request):
    case getType(deleteProjectAsync.success):
    case getType(deleteProjectAsync.failure):
    case getType(clearDeleteProject):
      return deleteProjectReducer(state, action);

    case getType(updateProjectMetaAsync.request):
    case getType(updateProjectMetaAsync.success):
    case getType(updateProjectMetaAsync.failure):
      return updateProjectMetaReducer(state, action);

    case getType(updateEmbedStatus):
    case getType(updateProjectHostingId):
    case getType(updateProjectAsync.request):
    case getType(updateProjectAsync.success):
    case getType(updateProjectAsync.failure):
      return updateProjectReducer(state, action);

    case getType(duplicateProjectAsync.request):
    case getType(duplicateProjectAsync.success):
    case getType(duplicateProjectAsync.failure):
      return duplicateProjectReducer(state, action);

    case getType(createProjectAsync.request):
    case getType(createProjectAsync.success):
    case getType(createProjectAsync.failure):
    case getType(addProject):
    case getType(clearCreatedProject):
    case getType(updateProjectPreview):
    case getType(updateCreatedProject):
    case getType(cancelCreateProject):
    case getType(removeProjectFromList):
      return createProjectReducer(state, action);

    case getType(videoFolderActions.fetchFolderByIdAsync.success): {
      const data = action.payload;
      return {
        ...state,
        data: findAndUpdateFolder(state.data!, data.id)(() => data),
        fetchFolderById: {
          ...state.fetchFolderById,
          data,
          isPending: false,
          isError: false
        }
      };
    }

    default:
      return reduceFactory(initialVideoFolderState, {
        ...asyncEntityHandlers<VideoFolderState>(videoFolderActions.fetchFolderByIdAsync, 'fetchFolderById')
      })(state, action);
  }
};

export default videoFolderReducer;
